import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Switch, FormControlLabel } from '@material-ui/core'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import SydModal from '../../../commonDesign/SydModal'
import SydModalActions from '../../../commonDesign/SydModal/SydModalActions'
import SydButton from '../../../commonDesign/Button'
import { GROUPING_OPTIONS } from '../lib/constants'
import GroupingCatalogPanel from './GroupingCatalogPanel'
import DraggableGroupingRow from './DraggableGroupingRow'
import GroupingDetailPanel from './GroupingDetailPanel'
import useStyles from './groupingsAndFiltersStyles'

/**
 * Groupings & Filters dialog (SMT-1608 Phase 1, replacing the previous
 * CustomGroupingDialog). Three blades:
 *   1. Catalog of available groupings (left)
 *   2. Selected groupings, drag to reorder, click to focus (middle)
 *   3. Detail panel for the focused grouping -- shows the FilterBuilder
 *      (right)
 *
 * The dialog is a deferred-apply form: edits accumulate locally and only
 * commit to the explorer config when the user clicks Apply. Cancel /
 * Close discards.
 */
export default function GroupingsAndFiltersDialog ({
  open,
  onClose,
  currentGroupings,
  currentScopeFilters,
  currentIncludeBenchmarks,
  scopeFiltersEnabled = true,
  onApply
}) {
  const classes = useStyles()
  const [selected, setSelected] = useState(currentGroupings || [])
  const [scopeFilters, setScopeFilters] = useState(currentScopeFilters || {})
  const [includeBenchmarks, setIncludeBenchmarks] = useState(!!currentIncludeBenchmarks)
  const [focusedGrouping, setFocusedGrouping] = useState(null)

  // Resync local draft on open. Cancel discards in-flight edits, so
  // re-opening must always start from the latest committed state.
  useEffect(() => {
    if (open) {
      setSelected(currentGroupings || [])
      setScopeFilters(currentScopeFilters || {})
      setIncludeBenchmarks(!!currentIncludeBenchmarks)
      setFocusedGrouping(null)
    }
  }, [open, currentGroupings, currentScopeFilters, currentIncludeBenchmarks])

  const handleAddGrouping = useCallback((value) => {
    setSelected(prev => prev.includes(value) ? prev : [...prev, value])
  }, [])

  const handleRemoveGrouping = useCallback((value) => {
    setSelected(prev => prev.filter(g => g !== value))
    setScopeFilters(prev => {
      if (!(value in prev)) return prev
      const next = { ...prev }
      delete next[value]
      return next
    })
    setFocusedGrouping(cur => cur === value ? null : cur)
  }, [])

  const moveItem = useCallback((fromIndex, toIndex) => {
    setSelected(prev => {
      const next = [...prev]
      const [moved] = next.splice(fromIndex, 1)
      next.splice(toIndex, 0, moved)
      return next
    })
  }, [])

  const handleSetFilter = useCallback((grouping, filter) => {
    setScopeFilters(prev => {
      const next = { ...prev }
      if (filter == null) delete next[grouping]
      else next[grouping] = filter
      return next
    })
  }, [])

  const handleApply = useCallback(() => {
    // Strip filters whose grouping is no longer selected -- defensive,
    // since the remove handler already does this.
    const allowed = new Set(selected)
    const cleanFilters = {}
    for (const [key, value] of Object.entries(scopeFilters)) {
      if (allowed.has(key) && value != null) cleanFilters[key] = value
    }
    onApply({ groupings: selected, scopeFilters: cleanFilters, includeBenchmarks })
    onClose()
  }, [selected, scopeFilters, includeBenchmarks, onApply, onClose])

  const focusedFilter = focusedGrouping ? scopeFilters[focusedGrouping] : null

  const availableGroupings = useMemo(() => {
    const selectedSet = new Set(selected)
    return GROUPING_OPTIONS.map(opt => ({ ...opt, alreadyAdded: selectedSet.has(opt.value) }))
  }, [selected])

  return (
    <SydModal
      open={open}
      onClose={onClose}
      title='Groupings & Filters'
      size={scopeFiltersEnabled ? 'large' : 'medium'}
    >
      <div className={classes.dialogLayout}>
        <DndProvider backend={HTML5Backend}>
          <div className={classes.body}>
            <GroupingCatalogPanel
              available={availableGroupings}
              onAdd={handleAddGrouping}
              classes={classes}
            />

            <div className={classes.selectedPanel}>
              <div className={classes.panelHeader}>
                {scopeFiltersEnabled
                  ? 'Selected (drag to reorder, click to filter)'
                  : 'Selected (drag to reorder)'}
              </div>
              <div className={classes.selectedList}>
                {selected.length === 0 && (
                  <div className={classes.emptyHint}>Click a grouping on the left to add it</div>
                )}
                {selected.map((grouping, index) => (
                  <DraggableGroupingRow
                    key={grouping}
                    grouping={grouping}
                    index={index}
                    isSelected={grouping === focusedGrouping}
                    filter={scopeFilters[grouping]}
                    moveItem={moveItem}
                    onFocus={setFocusedGrouping}
                    onRemove={handleRemoveGrouping}
                    classes={classes}
                    showFilterBadge={scopeFiltersEnabled}
                  />
                ))}
              </div>
              <div className={classes.optionsRow}>
                <FormControlLabel
                  className={classes.optionLabel}
                  control={(
                    <Switch
                      checked={includeBenchmarks}
                      onChange={(e) => setIncludeBenchmarks(e.target.checked)}
                      size='small'
                      color='primary'
                      inputProps={{ 'aria-label': 'Include assigned benchmarks' }}
                    />
                  )}
                  label='Include assigned benchmarks'
                />
                <div className={classes.optionHint}>
                  Adds a benchmark row beneath any item with an assigned benchmark. Performance columns show benchmark returns.
                </div>
              </div>
            </div>

            {scopeFiltersEnabled && (
              <GroupingDetailPanel
                grouping={focusedGrouping}
                filter={focusedFilter}
                onChange={handleSetFilter}
                classes={classes}
              />
            )}
          </div>
        </DndProvider>
      </div>
      <SydModalActions>
        <SydButton variant='outline' size='sm' onClick={onClose}>Cancel</SydButton>
        <SydButton
          variant='primary'
          size='sm'
          onClick={handleApply}
          disabled={selected.length === 0}
        >
          Apply
        </SydButton>
      </SydModalActions>
    </SydModal>
  )
}

GroupingsAndFiltersDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  currentGroupings: PropTypes.arrayOf(PropTypes.string),
  currentScopeFilters: PropTypes.object,
  currentIncludeBenchmarks: PropTypes.bool,
  scopeFiltersEnabled: PropTypes.bool,
  onApply: PropTypes.func.isRequired
}
